import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  User,
  ShoppingCart,
  Menu,
  X,
  ChevronDown,
  Heart,
  MapPin,
  Truck,
  PackageCheck,
  PackageSearch,
  MessageSquareQuote,
  LayoutGrid,
  Bell,
  ChevronRight,
  ChevronLeft,
  Home,
  Info,
  Package,
  Tag,
  Newspaper,
  LogOut
} from 'lucide-react';

// Maps a mobile nav label to an icon for the redesigned drawer.
const mobileNavIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('home')) return Home;
  if (n.includes('about')) return Info;
  if (n.includes('product')) return Package;
  if (n.includes('wholesale')) return Truck;
  if (n.includes('private') || n.includes('label')) return Tag;
  if (n.includes('blog') || n.includes('news')) return Newspaper;
  if (n.includes('quote')) return MessageSquareQuote;
  return LayoutGrid;
};
import { useSettings } from '../../hooks/useSettings';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import NotificationsDropdown from './NotificationsDropdown';
import { API_BASE } from '../../utils/apiConfig';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCurrencyOpen, setIsCurrencyOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [isMobileProfileOpen, setIsMobileProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [user, setUser] = useState(null);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, header, currencyCode, currencySymbol, setDisplayCurrency, currencyOptions } = useSettings();
  const { cartCount, setIsCartOpen } = useCart();
  const { favoriteItems } = useFavorites();

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return 'https://placehold.co/200x50?text=Logo';
    if (logoPath.startsWith('http')) return logoPath;
    if (logoPath.startsWith('/uploads')) {
      // Logo renders ~80px tall — request a small resized WebP instead of the
      // full multi-hundred-KB upload so it loads instantly.
      return `${API_BASE}${logoPath}?w=240`;
    }
    return logoPath;
  };

  const navScrollRef = useRef(null);
  const [navCanScrollLeft, setNavCanScrollLeft] = useState(false);
  const [navCanScrollRight, setNavCanScrollRight] = useState(false);

  const updateNavScrollState = useCallback(() => {
    const el = navScrollRef.current;
    if (!el) return;
    setNavCanScrollLeft(el.scrollLeft > 4);
    setNavCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scrollNav = (dir) => {
    const el = navScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  // Initialize nav scroll arrow state after render (delay lets nav DOM settle)
  useEffect(() => {
    const t = setTimeout(updateNavScrollState, 150);
    return () => clearTimeout(t);
  }, [updateNavScrollState]);

  // Mouse wheel on nav converts vertical scroll → horizontal scroll
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
      updateNavScrollState();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [updateNavScrollState]);

  // Drag to scroll on nav
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let scrollStart = 0;

    const onMouseDown = (e) => {
      isDown = true;
      startX = e.pageX - el.offsetLeft;
      scrollStart = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    };
    const onMouseLeave = () => {
      isDown = false;
      el.style.cursor = 'grab';
      el.style.userSelect = '';
    };
    const onMouseUp = () => {
      isDown = false;
      el.style.cursor = 'grab';
      el.style.userSelect = '';
    };
    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = scrollStart - (x - startX);
      updateNavScrollState();
    };

    el.style.cursor = 'grab';
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
    };
  }, [updateNavScrollState]);

  const [detectedLocation, setDetectedLocation] = useState(null);
  const [autoSelectedLocation, setAutoSelectedLocation] = useState(null);
  // Only treat localStorage value as manual if the user explicitly clicked a country
  const [manuallySelectedLocation, setManuallySelectedLocation] = useState(() => {
    const isManual = localStorage.getItem('user_location_manual') === 'true';
    if (!isManual) {
      // Clear old auto-saved value so geolocation can re-detect properly
      localStorage.removeItem('user_location');
      return null;
    }
    return localStorage.getItem('user_location');
  });

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('sidebar-open');
    window.dispatchEvent(new CustomEvent('sidebar-state', { detail: { open: true } }));
    return () => {
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('sidebar-open');
      window.dispatchEvent(new CustomEvent('sidebar-state', { detail: { open: false } }));
    };
  }, [isMobileMenuOpen]);

  // Auto-close the mobile drawer when the viewport grows to desktop (lg).
  // Without this, opening the menu on a narrow width and then widening the
  // window leaves the backdrop + body scroll-lock stuck on desktop.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setIsMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Safety net: if this component ever unmounts while the menu is open,
  // make sure the scroll-lock classes don't survive on <body>.
  useEffect(() => () => {
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('sidebar-open');
  }, []);

  useEffect(() => {
    const fetchLocation = async () => {
      // Skip if user manually chose a location
      if (localStorage.getItem('user_location_manual') === 'true') return;

      // Use cached detected location if already fetched before
      const cached = localStorage.getItem('detected_location_cache');
      if (cached) {
        try {
          setDetectedLocation(JSON.parse(cached));
        } catch (_) {}
        return;
      }

      // First time only — call the API
      try {
        const res = await fetch('https://ipinfo.io/json');
        const data = await res.json();
        if (data && data.city && !data.error) {
          const countryNames = { IN: 'India', GB: 'United Kingdom', US: 'United States', DE: 'Germany', AE: 'UAE', AU: 'Australia', CA: 'Canada', FR: 'France', SG: 'Singapore', NZ: 'New Zealand', ZA: 'South Africa', PK: 'Pakistan', BD: 'Bangladesh', NL: 'Netherlands', IT: 'Italy', ES: 'Spain', JP: 'Japan', CN: 'China', BR: 'Brazil', MX: 'Mexico' };
          const countryName = countryNames[data.country] || data.country;
          const displayStr = data.city ? `${data.city}, ${data.region || countryName}` : countryName;
          const loc = { full: displayStr, country: countryName };
          setDetectedLocation(loc);
          localStorage.setItem('detected_location_cache', JSON.stringify(loc));
        }
      } catch (error) {
        console.warn('Geolocation service unavailable');
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    const syncUser = () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      } else {
        setUser(null);
      }
    };
    const fetchUnreadCount = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        const u = storedUser ? JSON.parse(storedUser) : null;
        const userId = u ? (u.u_id || u.user_id || u.id) : '';
        const res = await fetch(`/api/notifications/public${userId ? `?userId=${userId}` : ''}`);
        const data = await res.json();
        if (u && Array.isArray(data)) {
          setUnreadNotifications(data.filter(n => !n.read_by?.includes(userId)).length);
        } else {
          setUnreadNotifications(0);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    syncUser();
    fetchUnreadCount();
    fetchLocation();
    window.addEventListener('storage', syncUser);

    const interval = setInterval(fetchUnreadCount, 60000); // Check every minute

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('storage', syncUser);
      clearInterval(interval);
    };
  }, [location.pathname]);

  // Auto-select country from dropdown when IP geolocation detects it (never persisted to localStorage)
  useEffect(() => {
    if (manuallySelectedLocation || !detectedLocation?.country) return;
    const options = header?.deliverTo?.options || [];
    if (options.length === 0) return;
    const detected = detectedLocation.country.toLowerCase();
    const full = detectedLocation.full?.toLowerCase() || '';
    const match = options.find(opt => {
      const o = opt.toLowerCase();
      return o === detected || o.includes(detected) || detected.includes(o) || full.includes(o);
    });
    setAutoSelectedLocation(match || null);
  }, [detectedLocation, header, manuallySelectedLocation]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsProfileOpen(false);
    setIsLocationOpen(false);
    setIsNotificationsOpen(false);
    setIsMobileProfileOpen(false);

    // Sync search bar value from URL ?q= param when on /products page
    if (location.pathname.startsWith('/products')) {
      const q = new URLSearchParams(location.search).get('q') || '';
      setSearchQuery(q);
      if (q) setIsMobileSearchOpen(true);
      else setIsMobileSearchOpen(false);
    } else {
      setIsMobileSearchOpen(false);
    }
  }, [location.pathname, location.search]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    setIsProfileOpen(false);
    // Notify CartContext / FavoritesContext so they clear immediately (no refresh needed)
    window.dispatchEvent(new Event('storage'));
    navigate('/auth');
  };

  const getInitial = (name) => name ? name.charAt(0).toUpperCase() : '?';

  // Derived settings from header provider
  const navSubBar = header?.navSubBar || { enabled: true, bgColor: "#ffffff", textColor: "#0f172a", showAllCategories: true };
  const navLinks = header?.navLinks || [];
  const deliverTo = header?.deliverTo || { enabled: true, location: "United Kingdom", options: [] };
  const showTrackShipment = header?.showTrackShipment ?? true;
  const trackLink = header?.trackShipmentLink || "/track-order";
  const showRequestQuote = header?.showRequestQuote ?? true;
  const quoteLink = header?.requestQuoteLink || "/request-quote";

  const bannerStyle = {
    backgroundColor: branding.headerBgColor || '#ffffff',
    color: branding.headerTextColor || '#0f172a',
  };

  const primaryColor = branding.themeColor || '#f97316'; // Fallback to orange if not set

  return (
    <header
      className={`w-full sticky top-0 z-[70] transition-all duration-300 ${isScrolled ? 'shadow-[0_1px_15px_rgba(0,0,0,0.08)]' : ''}`}
      style={bannerStyle}
    >
      {/* Top Main Bar */}
      <div className="border-b border-slate-100 py-3 sm:py-1.5">
        <div className="container mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between gap-1.5 sm:gap-4 lg:gap-8">

            {/* Logo & Deliver To */}
            <div className="flex min-w-0 items-center gap-2 sm:gap-4 lg:gap-10">
              {/* Mobile Menu Trigger */}
              <button className="shrink-0 lg:hidden p-2 text-slate-900 -ml-2" onClick={() => setIsMobileMenuOpen(true)}>
                <Menu size={24} className="sm:hidden" />
                <Menu size={28} className="hidden sm:block" />
              </button>

              <Link to="/" className="shrink-0 flex items-center gap-2 sm:gap-2.5 transition-transform active:scale-95">
                {branding.logo && (
                  <img
                    src={getLogoUrl(branding.logo)}
                    alt={branding.appName}
                    className="h-12 sm:h-16 lg:h-20 w-auto object-contain shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                {!branding.logo && (
                  <div className="flex flex-col leading-none min-w-0">
                    <span className="max-w-[140px] truncate sm:max-w-none text-base sm:text-xl lg:text-2xl font-black tracking-tight text-slate-900">
                      {branding.appName || 'Create enterprise'}
                    </span>
                    {branding.tagline && (
                      <span className="hidden sm:block text-[10px] lg:text-xs font-medium text-slate-400 mt-1 truncate">
                        {branding.tagline}
                      </span>
                    )}
                  </div>
                )}
              </Link>

              {deliverTo.enabled !== false && (
                <div className="hidden md:flex shrink-0 items-center gap-2 cursor-pointer group relative">
                  <div className="p-1 sm:p-2 rounded-full bg-slate-50 group-hover:bg-slate-100 transition-colors" onClick={() => setIsLocationOpen(!isLocationOpen)}>
                    <MapPin size={18} className="text-slate-400 group-hover:text-slate-900 transition-colors hidden sm:block" />
                    <MapPin size={14} className="text-slate-400 group-hover:text-slate-900 transition-colors sm:hidden" />
                  </div>
                  <div className="flex flex-col" onClick={() => setIsLocationOpen(!isLocationOpen)}>
                    <span className="text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-slate-400 leading-none mb-0.5 sm:mb-1">Deliver to</span>
                    <div className="flex items-center gap-0.5 sm:gap-1">
                      <span className="text-[10px] sm:text-sm font-black text-slate-900 line-clamp-1 max-w-[80px] sm:max-w-[120px]">
                        {manuallySelectedLocation || autoSelectedLocation || detectedLocation?.full || deliverTo.location}
                      </span>
                      <ChevronDown size={12} className={`text-slate-400 transition-transform ${isLocationOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Location Selector Dropdown */}
                  <AnimatePresence>
                    {isLocationOpen && (
                      <>
                        <div className="fixed inset-0 z-[80]" onClick={() => { setIsLocationOpen(false); setLocationSearch(''); }} />
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute left-0 top-full mt-4 w-72 bg-white rounded-3xl shadow-2xl border border-slate-100 z-[90] overflow-hidden p-2"
                        >
                          <div className="px-3 py-3 border-b border-slate-50 mb-1">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Select Location</span>
                            {/* Detected location */}
                            {detectedLocation?.full && (
                              <button
                                className="w-full flex items-center gap-2 mb-2 px-2 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors text-left"
                                onClick={() => {
                                  const options = deliverTo.options || [];
                                  const detected = detectedLocation.country?.toLowerCase() || '';
                                  const full = detectedLocation.full?.toLowerCase() || '';
                                  const match = options.find(opt => {
                                    const o = opt.toLowerCase();
                                    return o === detected || o.includes(detected) || detected.includes(o) || full.includes(o);
                                  });
                                  if (match) {
                                    localStorage.setItem('user_location', match);
                                    localStorage.setItem('user_location_manual', 'true');
                                    setManuallySelectedLocation(match);
                                  }
                                  setIsLocationOpen(false);
                                  setLocationSearch('');
                                }}
                              >
                                <MapPin size={12} className="text-blue-500 shrink-0" />
                                <span className="text-[11px] font-bold text-blue-700 truncate">{detectedLocation.full}</span>
                                <span className="text-[9px] text-blue-400 shrink-0 ml-auto">Detected ↵</span>
                              </button>
                            )}
                            {/* Search input */}
                            <input
                              type="text"
                              value={locationSearch}
                              onChange={(e) => setLocationSearch(e.target.value)}
                              placeholder="Search location..."
                              autoFocus
                              className="w-full h-9 px-3 text-sm font-medium text-slate-900 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {(deliverTo.options || [])
                              .filter(opt => opt.toLowerCase().includes(locationSearch.toLowerCase()))
                              .map(option => (
                                <button
                                  key={option}
                                  className={`w-full text-left px-4 py-2.5 rounded-2xl text-sm font-bold transition-all ${(manuallySelectedLocation || autoSelectedLocation) === option ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                                  onClick={() => {
                                    localStorage.setItem('user_location', option);
                                    localStorage.setItem('user_location_manual', 'true');
                                    setManuallySelectedLocation(option);
                                    setAutoSelectedLocation(null);
                                    setIsLocationOpen(false);
                                    setLocationSearch('');
                                  }}
                                >
                                  {option}
                                </button>
                              ))}
                            {(deliverTo.options || []).filter(opt => opt.toLowerCase().includes(locationSearch.toLowerCase())).length === 0 && (
                              <p className="text-center text-xs text-slate-400 py-4">No location found</p>
                            )}
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Central Search Bar */}
            <div className="hidden lg:flex flex-1 min-w-[100px] max-w-2xl xl:max-w-3xl relative group">
              <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-slate-900 transition-colors">
                <Search size={20} />
              </div>
              <input
                type="text"
                placeholder="Search supplements, health goals or ingredients..."
                className="w-full bg-slate-50 border border-slate-100 rounded-full py-3.5 pl-14 pr-6 text-sm font-medium focus:ring-2 focus:ring-orange-200 focus:border-orange-200 outline-none transition-all placeholder:text-slate-400"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/products?q=${searchQuery}`)}
              />
            </div>

            {/* Right Actions */}
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-3 lg:gap-6">
              {/* Actions moved to sub-bar or refined below */}



              <div className="flex items-center gap-0.5 sm:gap-1.5 lg:gap-4">
                {/* Mobile search toggle */}
                <button
                  className="lg:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
                  onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                >
                  <Search size={22} />
                </button>

                {/* Alerts */}
                <div className="relative">
                  <button
                    onClick={() => {
                      setIsNotificationsOpen(!isNotificationsOpen);
                      if (!isNotificationsOpen) {
                        setUnreadNotifications(0);
                      }
                    }}
                    className="flex flex-col items-center gap-1 px-1.5 sm:px-2 py-1 text-slate-600 hover:text-slate-900 transition-colors relative group"
                  >
                    <Bell size={22} className="group-active:scale-90 transition-transform" />
                    <span className="hidden lg:block text-[10px] font-semibold leading-none">Alerts</span>
                    {unreadNotifications > 0 && (
                      <span className="absolute top-0 right-0 sm:right-1 w-4 h-4 bg-orange-500 text-white text-[9px] font-black rounded-full flex items-center justify-center border-2 border-white">
                        {unreadNotifications}
                      </span>
                    )}
                  </button>

                  <NotificationsDropdown
                    isOpen={isNotificationsOpen}
                    onClose={() => setIsNotificationsOpen(false)}
                    primaryColor={primaryColor}
                  />
                </div>

                {/* Currency switcher */}
                <div className="relative">
                  <button
                    onClick={() => setIsCurrencyOpen((v) => !v)}
                    className="flex items-center gap-1 px-2 sm:px-2.5 py-1.5 text-slate-600 hover:text-slate-900 transition-colors rounded-xl hover:bg-slate-50"
                    aria-label="Change currency"
                  >
                    <span className="text-[13px] font-black leading-none">{currencySymbol}</span>
                    <span className="hidden sm:block text-[11px] font-bold leading-none">{currencyCode}</span>
                    <ChevronDown size={13} className={`transition-transform ${isCurrencyOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {isCurrencyOpen && (
                      <>
                        <div className="fixed inset-0 z-[60]" onClick={() => setIsCurrencyOpen(false)} />
                        <motion.div
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          className="absolute right-0 mt-2 w-36 bg-white rounded-2xl shadow-2xl border border-slate-100 py-1.5 z-[61] overflow-hidden"
                        >
                          <p className="px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-slate-300">Currency</p>
                          {currencyOptions.map((code) => (
                            <button
                              key={code}
                              onClick={() => { setDisplayCurrency(code); setIsCurrencyOpen(false); }}
                              className={`w-full flex items-center justify-between px-3 py-2 text-left text-xs font-bold transition-colors ${code === currencyCode ? 'text-orange-600 bg-orange-50' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                              <span>{code}</span>
                              <span className="text-slate-400">{({ INR: '₹', USD: '$', GBP: '£', EUR: '€', AED: 'د.إ', AUD: 'A$', CAD: 'C$' })[code] || ''}</span>
                            </button>
                          ))}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>

                {/* Wishlist */}
                <button
                  onClick={() => navigate('/favorites')}
                  className="flex flex-col items-center gap-1 px-1.5 sm:px-2 py-1 text-slate-600 hover:text-slate-900 transition-colors relative group"
                >
                  <Heart size={22} className="group-active:scale-90 transition-transform" />
                  <span className="hidden lg:block text-[10px] font-semibold leading-none">Wishlist</span>
                  {favoriteItems.length > 0 && (
                    <span className="absolute top-0 right-0 sm:right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center border-2 border-white">
                      {favoriteItems.length}
                    </span>
                  )}
                </button>

                {/* Account */}
                <div className="relative">
                  {user ? (
                    <button
                      onClick={() => setIsProfileOpen(!isProfileOpen)}
                      className="flex flex-col items-center gap-1 px-1.5 sm:px-2 py-1 text-slate-600 hover:text-slate-900 transition-colors group"
                    >
                      <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[11px] font-black overflow-hidden ring-1 ring-slate-100" style={{ backgroundColor: user.img ? '#ffffff' : primaryColor, color: user.img ? primaryColor : '#ffffff' }}>
                        {user.img ? (
                          <img src={user.img} alt={user.name} className="w-full h-full object-contain p-0.5" />
                        ) : (
                          getInitial(user.name)
                        )}
                      </div>
                      <span className="hidden lg:block text-[10px] font-semibold leading-none">Account</span>
                    </button>
                  ) : (
                    <Link
                      to="/auth"
                      className="flex flex-col items-center gap-1 px-1.5 sm:px-2 py-1 text-slate-600 hover:text-slate-900 transition-colors group"
                    >
                      <User size={22} className="group-active:scale-90 transition-transform" />
                      <span className="hidden lg:block text-[10px] font-semibold leading-none">Account</span>
                    </Link>
                  )}
                </div>

                {/* Cart */}
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="flex flex-col items-center gap-1 px-1.5 sm:px-2 py-1 text-slate-600 hover:text-slate-900 transition-colors relative group"
                >
                  <ShoppingCart size={22} className="group-active:scale-90 transition-transform" />
                  <span className="hidden lg:block text-[10px] font-semibold leading-none">Cart</span>
                  <span className="absolute top-0 right-0 sm:right-1 min-w-[16px] h-4 px-1 text-white text-[9px] font-black rounded-full flex items-center justify-center border-2 border-white" style={{ backgroundColor: primaryColor }}>
                    {cartCount}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Search Bar Expansion */}
          <AnimatePresence>
            {isMobileSearchOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="lg:hidden pt-3 sm:pt-4 pb-2"
              >
                <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-2 group focus-within:ring-2 focus-within:ring-slate-100 transition-all">
                  <Search size={18} className="text-slate-400 group-focus-within:text-slate-900 transition-colors shrink-0" />
                  <input
                    autoFocus
                    type="text"
                    placeholder="Search premium products..."
                    className="flex-1 bg-transparent border-none outline-none text-sm font-medium placeholder:text-slate-400"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (navigate(`/products?q=${searchQuery}`), setIsMobileSearchOpen(false))}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>



      {/* Bottom Sub-Bar */}
      {navSubBar.enabled && (
        <div
          className="hidden lg:block py-2 relative border-b border-slate-100 shadow-[0_1px_5px_rgba(0,0,0,0.02)]"
          style={{ backgroundColor: navSubBar.bgColor }}
        >
          <div className="container mx-auto px-8 relative">
            <div className="flex items-center gap-6">
              {/* Discover Trigger */}
              {navSubBar.showAllCategories && (
                <Link
                  to="/categories"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors whitespace-nowrap shrink-0"
                >
                  <Menu size={16} />
                  Shop by Category
                </Link>
              )}

              {/* Scrollable Navigation Area with arrow buttons */}
              <div className="relative flex-1 min-w-0 flex items-center">
                {navCanScrollLeft && (
                  <button
                    onClick={() => scrollNav(-1)}
                    className="absolute left-0 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white shadow-md border border-slate-100 text-slate-600 hover:bg-slate-50 transition-all shrink-0"
                  >
                    <ChevronLeft size={14} />
                  </button>
                )}
                <div
                  ref={navScrollRef}
                  onScroll={updateNavScrollState}
                  className="flex-1 min-w-0 overflow-x-auto no-scrollbar py-1"
                >
                  <nav className="flex items-center gap-x-5 min-w-max px-1">
                    {[...navLinks, { name: 'Blog', href: '/blog' }, { name: 'Request Quote', href: quoteLink }].map((link) => {
                      const isActive = location.pathname === link.href;
                      return (
                        <Link
                          key={link.name}
                          to={link.href}
                          className={`relative text-sm font-medium transition-colors px-2 py-3 whitespace-nowrap ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                          {link.name}
                          {isActive && (
                            <motion.span
                              layoutId="navUnderline"
                              className="absolute left-2 right-2 -bottom-px h-[2.5px] rounded-full"
                              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                              style={{ backgroundColor: primaryColor }}
                            />
                          )}
                        </Link>
                      );
                    })}
                  </nav>
                </div>
                {navCanScrollRight && (
                  <button
                    onClick={() => scrollNav(1)}
                    className="absolute right-0 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white shadow-md border border-slate-100 text-slate-600 hover:bg-slate-50 transition-all shrink-0"
                  >
                    <ChevronRight size={14} />
                  </button>
                )}
              </div>

              {showTrackShipment && (
                <button
                  onClick={() => navigate(trackLink)}
                  className="hidden lg:flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 font-black text-[10px] uppercase tracking-widest hover:border-slate-900 hover:bg-slate-50 transition-all active:scale-95 whitespace-nowrap"
                  style={{ color: navSubBar.textColor }}
                >
                  <Truck size={16} />
                  TRACK SHIPMENT
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[110]"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 220 }}
              className="fixed left-0 top-0 h-full w-full max-w-[330px] sm:max-w-[370px] bg-white z-[120] shadow-2xl flex flex-col rounded-r-[2rem] overflow-hidden"
            >
              {/* ── Emerald header block ── */}
              <div className="relative bg-gradient-to-br from-orange-600 to-orange-800 px-5 pt-6 pb-5 text-white">
                <div className="pointer-events-none absolute -top-16 -right-12 w-52 h-52 rounded-full bg-white/10 blur-3xl" />
                <div className="relative flex items-center justify-between mb-5">
                  <span className="font-black text-base uppercase tracking-[0.25em]">Menu</span>
                  <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 rounded-full bg-white/15 hover:bg-white/25 transition-colors">
                    <X size={18} />
                  </button>
                </div>

                {/* Profile / Guest card */}
                {user ? (
                  <div className="relative">
                    <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur rounded-2xl border border-white/15">
                      <div
                        onClick={() => setIsMobileProfileOpen(!isMobileProfileOpen)}
                        className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-lg font-black cursor-pointer overflow-hidden shrink-0 text-orange-700"
                      >
                        {user.img ? (
                          <img src={user.img} alt={user.name} className="w-full h-full object-cover" />
                        ) : (
                          getInitial(user.name)
                        )}
                      </div>
                      <div onClick={() => setIsMobileProfileOpen(!isMobileProfileOpen)} className="flex flex-col min-w-0 flex-1 cursor-pointer">
                        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-orange-100/80 leading-none mb-1">Authenticated</span>
                        <span className="text-sm font-black truncate">{user.name}</span>
                      </div>
                      {false && (
                      <button
                        onClick={() => setIsLocationOpen(!isLocationOpen)}
                        className="px-2.5 py-2 bg-white/15 hover:bg-white/25 rounded-xl text-white flex items-center gap-1 transition-colors"
                      >
                        {user.country || manuallySelectedLocation || detectedLocation?.country ? (
                          <span className="text-[11px] font-black">{user.country || manuallySelectedLocation || detectedLocation?.country}</span>
                        ) : (
                          <MapPin size={16} />
                        )}
                      </button>
                      )}
                    </div>

                    <AnimatePresence>
                      {isMobileProfileOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden mt-2 bg-white/10 backdrop-blur rounded-2xl border border-white/15 p-1.5"
                        >
                          <button onClick={() => { navigate('/orders'); setIsMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold text-white hover:bg-white/10 transition-colors">
                            <PackageSearch size={16} className="text-orange-100" /> Order History
                          </button>
                          <button onClick={() => { navigate('/profile'); setIsMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold text-white hover:bg-white/10 transition-colors">
                            <User size={16} className="text-orange-100" /> Profile Settings
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur rounded-2xl border border-white/15">
                    <div className="w-12 h-12 rounded-2xl bg-white/90 flex items-center justify-center text-orange-600 shrink-0">
                      <User size={22} />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-orange-100/80 leading-none mb-1">Guest</span>
                      <span className="text-sm font-black truncate">Welcome{branding?.appName ? `, ${branding.appName}` : ''}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Nav links ── */}
              <div className="flex-1 overflow-y-auto py-4 px-4 space-y-1.5">
                {[...navLinks, { name: 'Blog', href: '/blog' }].map((link) => {
                  const Icon = mobileNavIcon(link.name);
                  return (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-3.5 px-3 py-3 rounded-2xl text-sm font-black text-slate-800 hover:bg-orange-50 hover:text-orange-700 transition-all group"
                    >
                      <span className="w-9 h-9 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center group-hover:bg-orange-600 group-hover:text-white transition-colors shrink-0">
                        <Icon size={16} />
                      </span>
                      <span className="flex-1 uppercase tracking-tight">{link.name}</span>
                      <ChevronRight size={16} className="text-slate-300 group-hover:text-orange-500" />
                    </Link>
                  );
                })}

                {/* Currency switcher */}
                <div className="pt-3 mt-2 border-t border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-2 ml-1">Currency</p>
                  <div className="flex flex-wrap gap-1.5">
                    {currencyOptions?.map((code) => (
                      <button
                        key={code}
                        onClick={() => setDisplayCurrency(code)}
                        className={`px-3 py-1.5 rounded-xl text-[11px] font-black transition-all ${code === currencyCode ? 'bg-orange-600 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                      >
                        {code}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Bottom actions ── */}
              <div className="p-4 border-t border-slate-100 bg-slate-50/70 space-y-2.5">
                <button
                  onClick={() => { navigate(quoteLink); setIsMobileMenuOpen(false); }}
                  className="w-full py-3.5 rounded-2xl text-white font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-orange-600/25 bg-gradient-to-br from-orange-600 to-orange-700 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                >
                  <MessageSquareQuote size={16} /> Request Quote
                </button>
                {!user ? (
                  <div className="flex gap-2.5">
                    <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)} className="flex-1 text-center py-3.5 rounded-2xl bg-white border border-slate-200 font-black text-[11px] uppercase tracking-widest text-slate-700">Login</Link>
                    <Link to="/auth?mode=signup" onClick={() => setIsMobileMenuOpen(false)} className="flex-1 text-center py-3.5 rounded-2xl bg-slate-900 text-white font-black text-[11px] uppercase tracking-widest">Join</Link>
                  </div>
                ) : (
                  <button onClick={handleLogout} className="w-full py-3.5 rounded-2xl bg-red-50 text-red-600 font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2">
                    <LogOut size={15} /> Sign Out
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Profile Dropdown */}
      <AnimatePresence>
        {isProfileOpen && user && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute right-0 sm:right-8 top-full mt-2 w-[min(90vw,18rem)] bg-white rounded-3xl shadow-2xl border border-slate-100 z-[100] overflow-hidden py-4"
          >
            <div className="px-6 py-4 border-b border-slate-50 mb-2">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Authenticated Account</p>
              <p className="font-black text-slate-900 truncate">{user.name}</p>
              <p className="text-xs font-medium text-slate-500 truncate">{user.email_id}</p>
            </div>
            <div className="px-2 pb-2">
              <button onClick={() => { navigate('/orders'); setIsProfileOpen(false); }} className="w-full flex items-center gap-4 px-4 py-3 rounded-2xl text-sm font-bold text-slate-700 hover:bg-slate-50 transition-all">
                <PackageSearch size={18} className="text-slate-400" />
                Order History
              </button>
              <button onClick={() => { navigate('/profile'); setIsProfileOpen(false); }} className="w-full flex items-center gap-4 px-4 py-3 rounded-2xl text-sm font-bold text-slate-700 hover:bg-slate-50 transition-all">
                <User size={18} className="text-slate-400" />
                Profile Settings
              </button>
              <div className="h-px bg-slate-50 my-2 mx-4" />
              <button onClick={handleLogout} className="w-full flex items-center gap-4 px-4 py-3 rounded-2xl text-sm font-black text-red-600 hover:bg-red-50 transition-all">
                <PackageCheck size={18} className="text-red-400" />
                Log out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
